import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  listPiiRequests,
  approvePiiRequest,
  declinePiiRequest,
} from '../../api/piiAccessApi';
import './PiiAccessRequestsModal.css';

/**
 * PiiAccessRequestsModal
 *
 * Admin interface to approve or decline PII access requests from drivers.
 * Features:
 *   - Tab filtering (Pending / All)
 *   - Custom duration selection per request (default 60 min)
 *   - Custom decline reason and cooldown selection (default 24h)
 *   - Live action feedback with loading states
 */
const PiiAccessRequestsModal = ({ isOpen, onClose, onActionCompleted }) => {
  const [filterStatus, setFilterStatus] = useState('pending'); // 'pending' | 'all'
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null); // requestId
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Per-request selected duration (minutes): { [requestId]: number }
  const [durations, setDurations] = useState({});

  // Decline dialog state: { requestId, reason, cooldownHours } | null
  const [declineTarget, setDeclineTarget] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const data = await listPiiRequests(filterStatus === 'pending' ? 'pending' : null);
      setRequests(data || []);
      // Initialize default duration (60m) for pending requests
      const initDurations = {};
      (data || []).forEach((r) => {
        initDurations[r.id] = r.access_duration_m || 60;
      });
      setDurations(initDurations);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    if (isOpen) {
      fetchRequests();
    }
  }, [isOpen, fetchRequests]);

  if (!isOpen) return null;

  const handleDurationChange = (requestId, minutes) => {
    setDurations((prev) => ({ ...prev, [requestId]: Number(minutes) }));
  };

  const handleApprove = async (req) => {
    setActionInProgress(req.id);
    setErrorMsg('');
    setSuccessMsg('');
    const duration = durations[req.id] || 60;
    try {
      await approvePiiRequest(req.id, duration);
      setSuccessMsg(`Approved access for ${req.requester_email} (${duration} min)`);
      setTimeout(() => setSuccessMsg(''), 4000);
      await fetchRequests();
      if (onActionCompleted) onActionCompleted();
    } catch (err) {
      setErrorMsg(`Failed to approve: ${err.message}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const openDeclineDialog = (req) => {
    setDeclineTarget({
      requestId: req.id,
      email: req.requester_email,
      customer: req.customer_name || req.customer_actor_id,
      reason: '',
      cooldownHours: 24,
    });
  };

  const handleConfirmDecline = async () => {
    if (!declineTarget) return;
    setActionInProgress(declineTarget.requestId);
    setErrorMsg('');
    try {
      await declinePiiRequest(declineTarget.requestId, {
        declineReason: declineTarget.reason || 'Declined by dispatcher',
        cooldownHours: declineTarget.cooldownHours || 24,
      });
      setSuccessMsg(`Declined request with ${declineTarget.cooldownHours}h cooldown`);
      setTimeout(() => setSuccessMsg(''), 4000);
      setDeclineTarget(null);
      await fetchRequests();
      if (onActionCompleted) onActionCompleted();
    } catch (err) {
      setErrorMsg(`Failed to decline: ${err.message}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return '-';
    try {
      const d = new Date(isoStr);
      return d.toLocaleString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoStr;
    }
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <div className="pii-admin-backdrop" onClick={onClose}>
      <div className="pii-admin-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="pii-admin-header">
          <div className="pii-admin-title-row">
            <span className="pii-admin-icon">📋</span>
            <h2>Customer Contact Access Requests</h2>
            {pendingCount > 0 && (
              <span className="pii-admin-pending-pill">{pendingCount} pending</span>
            )}
          </div>
          <button type="button" className="pii-admin-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="pii-admin-tabs">
          <button
            type="button"
            className={`pii-admin-tab ${filterStatus === 'pending' ? 'active' : ''}`}
            onClick={() => setFilterStatus('pending')}
          >
            Pending Requests
          </button>
          <button
            type="button"
            className={`pii-admin-tab ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            All History
          </button>
          <button
            type="button"
            className="pii-admin-tab-refresh"
            onClick={fetchRequests}
            disabled={loading}
            title="Refresh list"
          >
            🔄 Refresh
          </button>
        </div>

        {/* Alerts */}
        {errorMsg && <div className="pii-admin-alert error">{errorMsg}</div>}
        {successMsg && <div className="pii-admin-alert success">{successMsg}</div>}

        {/* Requests List */}
        <div className="pii-admin-body">
          {loading && (
            <div className="pii-admin-loading">
              <span className="pii-spinner-sm" /> Loading requests...
            </div>
          )}

          {!loading && requests.length === 0 && (
            <div className="pii-admin-empty">
              <span>🎉</span>
              <p>No {filterStatus === 'pending' ? 'pending' : ''} access requests.</p>
            </div>
          )}

          {!loading &&
            requests.map((req) => {
              const isPending = req.status === 'pending';
              const isBusy = actionInProgress === req.id;
              const selectedDuration = durations[req.id] || 60;

              return (
                <div
                  key={req.id}
                  className={`pii-admin-card status-${req.status}`}
                >
                  <div className="pii-card-top">
                    <div className="pii-card-requester">
                      <strong>👤 {req.requester_email}</strong>
                      <span className="pii-card-date">
                        {formatDateTime(req.requested_at)}
                      </span>
                    </div>
                    <span className={`pii-card-status-badge ${req.status}`}>
                      {req.status === 'pending' && '⏳ Pending'}
                      {req.status === 'approved' && '✅ Approved'}
                      {req.status === 'declined' && '❌ Declined'}
                    </span>
                  </div>

                  <div className="pii-card-details">
                    <div>
                      <span className="pii-detail-label">Customer:</span>{' '}
                      <strong>{req.customer_name || 'Customer'}</strong>{' '}
                      <span className="pii-detail-id">({req.customer_actor_id})</span>
                    </div>
                    {req.process_id && (
                      <div>
                        <span className="pii-detail-label">Process ID:</span>{' '}
                        <code>{req.process_id}</code>
                      </div>
                    )}
                    {req.reason && (
                      <div className="pii-card-reason">
                        <span className="pii-detail-label">Reason:</span> &ldquo;{req.reason}&rdquo;
                      </div>
                    )}
                  </div>

                  {/* Resolution info for past requests */}
                  {!isPending && (
                    <div className="pii-card-resolved-info">
                      {req.status === 'approved' && (
                        <span>
                          Granted for {req.access_duration_m || 60} min by{' '}
                          {req.resolved_by_email || 'admin'} (expires{' '}
                          {formatDateTime(req.access_expires_at)})
                        </span>
                      )}
                      {req.status === 'declined' && (
                        <span>
                          Declined by {req.resolved_by_email || 'admin'}
                          {req.decline_reason ? ` (${req.decline_reason})` : ''} — Cooldown until{' '}
                          {formatDateTime(req.cooldown_until)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions for Pending Requests */}
                  {isPending && (
                    <div className="pii-card-actions">
                      <div className="pii-duration-select-wrap">
                        <label className="pii-duration-label">
                          Access Duration:
                          <select
                            className="pii-duration-select"
                            value={selectedDuration}
                            onChange={(e) =>
                              handleDurationChange(req.id, e.target.value)
                            }
                            disabled={isBusy}
                          >
                            <option value={30}>30 minutes</option>
                            <option value={60}>1 hour (Default)</option>
                            <option value={120}>2 hours</option>
                            <option value={240}>4 hours</option>
                            <option value={480}>8 hours</option>
                          </select>
                        </label>
                      </div>

                      <div className="pii-action-btns">
                        <button
                          type="button"
                          className="pii-decline-btn"
                          onClick={() => openDeclineDialog(req)}
                          disabled={isBusy}
                        >
                          ✕ Decline
                        </button>
                        <button
                          type="button"
                          className="pii-approve-btn"
                          onClick={() => handleApprove(req)}
                          disabled={isBusy}
                        >
                          {isBusy ? 'Saving...' : '✓ Approve Access'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* Decline Sub-dialog */}
        {declineTarget && (
          <div
            className="pii-subdialog-backdrop"
            onClick={() => setDeclineTarget(null)}
          >
            <div
              className="pii-subdialog"
              onClick={(e) => e.stopPropagation()}
            >
              <h4>❌ Decline Contact Access</h4>
              <p>
                Decline request from <strong>{declineTarget.email}</strong> for{' '}
                <strong>{declineTarget.customer}</strong>?
              </p>

              <label className="pii-subdialog-label">
                Decline Reason (shown to driver):
                <input
                  type="text"
                  className="pii-subdialog-input"
                  value={declineTarget.reason}
                  onChange={(e) =>
                    setDeclineTarget({
                      ...declineTarget,
                      reason: e.target.value,
                    })
                  }
                  placeholder="e.g. Customer requested privacy, driver not assigned"
                />
              </label>

              <label className="pii-subdialog-label">
                Cooldown Period (prevents spam re-requesting):
                <select
                  className="pii-subdialog-select"
                  value={declineTarget.cooldownHours}
                  onChange={(e) =>
                    setDeclineTarget({
                      ...declineTarget,
                      cooldownHours: Number(e.target.value),
                    })
                  }
                >
                  <option value={6}>6 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours (Default)</option>
                  <option value={48}>48 hours (2 days)</option>
                  <option value={168}>7 days</option>
                </select>
              </label>

              <div className="pii-subdialog-actions">
                <button
                  type="button"
                  className="pii-cancel-btn"
                  onClick={() => setDeclineTarget(null)}
                  disabled={actionInProgress === declineTarget.requestId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pii-confirm-decline-btn"
                  onClick={handleConfirmDecline}
                  disabled={actionInProgress === declineTarget.requestId}
                >
                  Confirm Decline
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PiiAccessRequestsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onActionCompleted: PropTypes.func.isRequired,
};

export default PiiAccessRequestsModal;
